


import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
} from '@mui/material';

import {
  ExpandMore,
  Help,
  ContactSupport,
  Search,
  Email,
  Chat,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

/* -------------------------------------
    FIXED + STRUCTURED FAQ LIST
-------------------------------------- */
const faqs = [
  {
    category: "Getting Started",
    question: "How do I create an account?",
    answer:
      "Sign up with your name, email address, and choose your location. We will send an OTP to verify your identity.",
  },
  {
    category: "Getting Started",
    question: "How do I log in?",
    answer:
      "Enter your email and password. A verification OTP will be sent to your email for secure login.",
  },
  {
    category: "Getting Started",
    question: "What is OTP verification?",
    answer:
      "OTP is a 6-digit code sent to your email. Enter it within 10 minutes to complete login or signup.",
  },
  {
    category: "File & Folder Management",
    question: "How do I create a folder?",
    answer:
      "Click 'Create Folder' and enter a name. You can optionally set a password for security.",
  },
  {
    category: "File & Folder Management",
    question: "How do I upload files?",
    answer:
      "Select files from your device and choose the folder you want to upload them to.",
  },
  {
    category: "Security & Access",
    question: "Is my data secure?",
    answer:
      "Yes. Files are encrypted, sessions are temporary, and all operations are logged securely.",
  },
  {
    category: "System Features",
    question: "Are my files stored permanently?",
    answer:
      "Yes, files are stored indefinitely unless you delete them manually.",
  },
  {
    category: "Troubleshooting",
    question: "What if uploads fail?",
    answer:
      "Check your internet connection and try again. Contact support if the issue continues.",
  },
  {
    category: "Support & Help",
    question: "How do I get help?",
    answer:
      "Use the help center, email support, or contact an admin for assistance.",
  },
];

const HelpCenter = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    message: '',
  });

  // Filter FAQ
  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Unique categories
  const categories = [...new Set(faqs.map((faq) => faq.category))];

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      setContactForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
      }));
    }
  }, [user]);

  const handleContactSubmit = async () => {
    if (!contactForm.message.trim()) {
      enqueueSnackbar('Please enter your message', { variant: 'warning' });
      return;
    }

    setContactLoading(true);
    try {
      const response = await api.post('/api/users/contact-support',{
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
      });

      if (response.data.success) {
        enqueueSnackbar(response.data.message, { variant: 'success' });
        setContactOpen(false);
        setContactForm(prev => ({ ...prev, message: '' }));
      } else {
        enqueueSnackbar('Failed to send message. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Contact support error:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to send message. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Help & Support Center
      </Typography>

      <Grid container spacing={3}>
        
        {/* Search Box */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Help color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">How can we help you?</Typography>
              </Box>

              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: 'action.active' }} />
                  ),
                }}
              />

              {/* Categories */}
              <Box display="flex" flexWrap="wrap" gap={1}>
                {categories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    variant="outlined"
                    onClick={() => setSearchTerm(category)}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* FAQ List */}
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Frequently Asked Questions
              </Typography>

              {filteredFaqs.map((faq, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box>
                      <Typography variant="subtitle1">{faq.question}</Typography>
                      <Chip
                        label={faq.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography>{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}

              {filteredFaqs.length === 0 && (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  No matching results found.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Support */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Support
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Can't find what you're looking for? Our support team is here to help.
              </Typography>

              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <Email />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Email Support"
                    secondary="creditklick21@gmail.com"
                  />
                </ListItem>

                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <Chat />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Live Chat"
                    secondary="Available 24/7"
                  />
                </ListItem>
              </List>

              <Button
                variant="contained"
                fullWidth
                startIcon={<ContactSupport />}
                sx={{ mt: 2 }}
                onClick={() => setContactOpen(true)}
              >
                Contact Us
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contact Modal */}
      <Dialog open={contactOpen} onClose={() => setContactOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Contact Support</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={contactForm.name}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
            helperText="Your name is automatically filled from your account"
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={contactForm.email}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
            helperText="Your email is automatically filled from your account"
          />
          <TextField
            margin="dense"
            label="Message"
            multiline
            rows={4}
            fullWidth
            value={contactForm.message}
            onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
            placeholder="Please describe your issue or question..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactOpen(false)} disabled={contactLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleContactSubmit}
            disabled={contactLoading || !contactForm.message.trim()}
            startIcon={contactLoading ? <CircularProgress size={20} /> : null}
          >
            {contactLoading ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HelpCenter;
